import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Snackbar from "@mui/material/Snackbar";
import { useState } from "react";
import {
  IMPACT_REPORT_BAR_NARROW_MAX_PX,
  IMPACT_REPORT_PDF_DOWNLOAD_ARIA_LABEL,
  IMPACT_REPORT_PDF_DOWNLOAD_ERROR_TOAST_MS,
  IMPACT_REPORT_PDF_DOWNLOAD_PENDING_LABEL,
} from "@/constants/impactReport";
import { useImpactReportContext } from "@/context/ImpactReportContext";
import { useImpactReportPdfDownload } from "@/hooks/useImpactReportPdfDownload";

/** Same 44px height as `ImpactFilterMenu`'s trigger, so the bar's controls line up. */
const CONTROL_HEIGHT = "44px";

/** The tier at which `ImpactFilterMenu` trims its squares from 44px to 40px. */
const NARROW_MEDIA_QUERY = `@media (max-width: ${IMPACT_REPORT_BAR_NARROW_MAX_PX}px)`;

/**
 * The control bar's download control. It FIRES an action rather than opening a menu, so it is not
 * an `ImpactFilterMenu` — but it is drawn as one of that family's buttons in their icon-only form
 * (same height, border, radius, padding and icon treatment), at every width. Its word would make
 * it the widest control on the bar, and every pixel it takes comes out of the pills' room; the
 * `aria-label` and `title` name it for assistive tech and on hover, and the closing page's button
 * carries the word where there is room for it.
 *
 * Shares `useImpactReportPdfDownload` with that button, so a download begun from either busies
 * both, and neither can start a second job while one is in flight.
 *
 * The button renders only in the hero view — the cards view draws no document to export. The hook,
 * the live region and the toast stay mounted in both views: the hook's subscription is what keeps
 * an in-flight download alive (the last unsubscribe abandons it), and a failure that lands after
 * the user switched views still has to be said.
 *
 * THE ONE PLACE A FAILURE IS SHOWN, for both affordances. This component is mounted for as long as
 * `/impact` is, and the closing page's button only ever renders on that route, so a second toast
 * there would announce every failure twice. A toast rather than inline text because the bar's geometry
 * is a fixed 64px at every width; the `Snackbar` is `position: fixed`, outside the bar's flow.
 */
export function ImpactReportDownloadControl() {
  const { viewMode } = useImpactReportContext();
  const { pending, error, canDownload, download } = useImpactReportPdfDownload();

  // Every attempt clears the shared error before it can set a new one, so a repeat of the same
  // failure still passes through `null` here and re-opens the toast.
  const [toastError, setToastError] = useState<string | null>(null);
  const [isToastOpen, setIsToastOpen] = useState(false);
  if (error !== toastError) {
    setToastError(error);
    setIsToastOpen(error !== null);
  }

  return (
    <>
      {viewMode === "hero" && (
        <Box
          component="button"
          type="button"
          onClick={download}
          disabled={!canDownload || pending}
          aria-busy={pending}
          aria-label={IMPACT_REPORT_PDF_DOWNLOAD_ARIA_LABEL}
          title={IMPACT_REPORT_PDF_DOWNLOAD_ARIA_LABEL}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            height: CONTROL_HEIGHT,
            px: "12px",
            // AFTER the base padding, as in `ImpactFilterMenu`: the later declaration wins.
            [NARROW_MEDIA_QUERY]: { px: "10px" },
            flexShrink: 0,
            borderRadius: "8px",
            border: "1px solid var(--color-smoke)",
            bgcolor: "var(--color-white)",
            color: "var(--color-rodeo-black)",
            cursor: "pointer",
            transition: "border-color 0.15s, background 0.15s",
            "&:hover:not(:disabled)": { borderColor: "var(--color-dark-dust)" },
            "&:focus-visible": {
              outline: "2px solid var(--color-primary-active)",
              outlineOffset: "2px",
            },
            "&:disabled": { opacity: 0.5, cursor: "default" },
            "& .MuiSvgIcon-root": { fontSize: 22, color: "var(--color-dark-dust)" },
          }}
        >
          {pending ? (
            // Sized to the icon it replaces, so the button does not change width mid-download.
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 22,
                height: 22,
              }}
            >
              <CircularProgress size={18} thickness={5} sx={{ color: "var(--color-dark-dust)" }} />
            </Box>
          ) : (
            <FileDownloadOutlined />
          )}
        </Box>
      )}

      <span role="status" aria-live="polite" className="sr-only">
        {pending ? IMPACT_REPORT_PDF_DOWNLOAD_PENDING_LABEL : ""}
      </span>

      <Snackbar
        open={isToastOpen}
        autoHideDuration={IMPACT_REPORT_PDF_DOWNLOAD_ERROR_TOAST_MS}
        onClose={() => setIsToastOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" variant="filled" onClose={() => setIsToastOpen(false)}>
          {toastError}
        </Alert>
      </Snackbar>
    </>
  );
}
